import {
  IImageReference,
  IImageReferenceInNote,
  IImageMentionItem,
} from "../@types/Note";

/**
 * Parses HTML content to find all image references
 * Image references are stored as: <span class="image-ref" data-image-key="{guid}" data-image-order="1">Image 1</span>
 */
export const parseImageReferences = (html: string): IImageReferenceInNote[] => {
  const references: IImageReferenceInNote[] = [];
  const regex =
    /<span[^>]*class="image-ref"[^>]*data-image-key="([^"]+)"[^>]*data-image-order="(\d+)"[^>]*>([^<]+)<\/span>/g;

  let match = regex.exec(html);
  while (match !== null) {
    references.push({
      imageKey: match[1],
      displayOrder: parseInt(match[2], 10),
      referenceText: match[3],
    });
    match = regex.exec(html);
  }

  return references;
};

/**
 * Converts image reference data to mention-compatible items for autocomplete
 */
export const imagesToMentionItems = (
  images: IImageReference[]
): IImageMentionItem[] => {
  return images.map((img) => ({
    id: img.key,
    value: `Image ${img.displayOrder}`,
    displayOrder: img.displayOrder,
    preSignedUrl: img.preSignedUrl,
  }));
};

/**
 * Inserts an image reference into the editor at the current cursor position
 */
export const createImageReferenceHTML = (
  imageKey: string,
  displayOrder: number
): string => {
  return `<span class="image-ref" data-image-key="${imageKey}" data-image-order="${displayOrder}" contenteditable="false">Image ${displayOrder}</span>`;
};

/**
 * Validates that all image references in the HTML point to existing images
 */
export const validateImageReferences = (
  html: string,
  availableImages: IImageReference[]
): { valid: boolean; missingReferences: string[] } => {
  const references = parseImageReferences(html);
  const availableKeys = new Set(availableImages.map((img) => img.key));

  const missingReferences = references
    .filter((ref) => !availableKeys.has(ref.imageKey))
    .map((ref) => ref.referenceText);

  return {
    valid: missingReferences.length === 0,
    missingReferences,
  };
};

/**
 * Replaces GUID-based image references with display order numbers
 * Used when loading existing notes with old-style references
 */
export const normalizeImageReferences = (
  html: string,
  imageMap: Map<string, number> // Map<imageKey, displayOrder>
): string => {
  let normalized = html;

  imageMap.forEach((displayOrder, imageKey) => {
    // Find old-style references without display order
    const oldStyleRegex = new RegExp(
      `<span[^>]*class="image-ref"[^>]*data-image-key="${imageKey}"[^>]*>([^<]*)</span>`,
      "g"
    );

    normalized = normalized.replace(oldStyleRegex, (match) => {
      // Check if it already has data-image-order
      if (match.includes("data-image-order=")) {
        return match; // Already normalized
      }

      // Add display order attribute
      return match.replace("<span", `<span data-image-order="${displayOrder}"`);
    });
  });

  return normalized;
};

/**
 * Removes all image references from HTML content
 * Useful for cleanup or when references become invalid
 */
export const removeImageReferences = (html: string): string => {
  return html.replace(/<span[^>]*class="image-ref"[^>]*>([^<]+)<\/span>/g, "");
};

/**
 * Finds the image associated with a given reference span element
 */
export const getImageFromReference = (
  referenceElement: HTMLElement,
  images: IImageReference[]
): IImageReference | null => {
  const { imageKey } = referenceElement.dataset;
  if (!imageKey) return null;

  return images.find((img) => img.key === imageKey) || null;
};

/**
 * Updates display order in existing references when images are reordered
 * (Currently not used as we use fixed numbering, but kept for future enhancement)
 */
export const updateReferenceOrders = (
  html: string,
  orderMap: Map<string, number> // Map<imageKey, newDisplayOrder>
): string => {
  let updated = html;

  orderMap.forEach((newOrder, imageKey) => {
    const regex = new RegExp(
      `(<span[^>]*class="image-ref"[^>]*data-image-key="${imageKey}"[^>]*data-image-order=")\\d+("[^>]*>)Image \\d+`,
      "g"
    );

    updated = updated.replace(regex, `$1${newOrder}$2Image ${newOrder}`);
  });

  return updated;
};

/**
 * Extracts all unique image keys referenced in the HTML
 */
export const getReferencedImageKeys = (html: string): string[] => {
  const references = parseImageReferences(html);
  return Array.from(new Set(references.map((ref) => ref.imageKey)));
};

/**
 * Counts how many times each image is referenced in the note
 */
export const getImageReferenceCounts = (html: string): Map<string, number> => {
  const references = parseImageReferences(html);
  const counts = new Map<string, number>();

  references.forEach((ref) => {
    counts.set(ref.imageKey, (counts.get(ref.imageKey) || 0) + 1);
  });

  return counts;
};
